import {
  DatabaseAuto, IdataArrayResponse, IfilterProps,
  Iitem,
  IitemInventory,
  IiventoryAnalytics,
  TitemInventoryRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';
import { Item } from './item.define';

/**
 * Type alias for item inventory properties with specific generic types
 * @template T - Type for quantity (default: number)
 * @template U - Type for ID (default: string)
 * @template V - Type for item (default: Iitem)
 * @template W - Type for commerce user (default: IcommerceUser)
 */
type TitemInventoryProps = IitemInventory<number, string, Iitem>;

/**
 * Represents an item's inventory record in the system.
 * This class provides methods to manage inventory levels, track stock movements,
 * and analyze inventory-related data.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TitemInventoryProps - The type definition for item inventory properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link ItemInventory} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const itemInventory = await ItemInventory.getAll();
 *
 * const itemInventory = await ItemInventory.filterAll({
 *
 * });
 *
 * const itemInventory = await ItemInventory.getOne('resourceId');
 *
 * ```
 */
export class ItemInventory extends DatabaseAuto {
  /** Base API endpoint for inventory operations */
  private static readonly baseUrl = '/item-inventory';

  /** The item this inventory record belongs to */
  readonly item: Item;

  /** The date of the inventory transaction */
  readonly date: TitemInventoryProps['date'];

  /** The quantity of items in this inventory record */
  readonly quantity: TitemInventoryProps['quantity'];

  /** Optional array of colors for this inventory item */
  readonly colors?: TitemInventoryProps['colors'];

  /** Optional array of sizes for this inventory item */
  readonly sizes?: TitemInventoryProps['sizes'];

  /** The cost per unit of the item */
  readonly unitCost: TitemInventoryProps['unitCost'];

  /** The total cost of this inventory transaction */
  readonly totalCost: TitemInventoryProps['totalCost'];

  /** The source of this inventory transaction */
  readonly source: TitemInventoryProps['source'];

  /** The remaining quantity of this inventory item */
  readonly quantityRemaining: TitemInventoryProps['quantityRemaining'];

  /**
   * Creates a new instance of ItemInventory.
   * @param {TitemInventoryProps} data - The inventory data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TitemInventoryProps) {
    validateData(data, {
      item: {
        validate: (v) => {
          if (typeof v === 'object') {
            return Boolean(v._id);
          }

          return false;
        },
        message: 'item must be a valid item object with an _id'
      },
      date: {
        validate: (v) => {
          // Convert to Date if not already a Date object
          const date = v instanceof Date ? v : new Date(v);

          // Check date validity
          return !isNaN(date.getTime());
        },
        message: 'date must be a valid date'
      },
      unitCost: {
        validate: (v) => typeof v === 'number',
        message: 'unitCost must be a number'
      },
      totalCost: {
        validate: (v) => typeof v === 'number',
        message: 'totalCost must be a number'
      },
      source: {
        validate: (v) => typeof v === 'string' && v.length > 0,
        message: 'source must be a non-empty string'
      }
    });

    super(data);

    this.item = new Item(data.item);
    this.date = new Date(data.date);
    this.quantity = data.quantity;
    this.colors = data.colors || [];
    this.sizes = data.sizes || [];
    this.unitCost = data.unitCost;
    this.totalCost = data.totalCost;
    this.source = data.source;
    this.quantityRemaining = data.quantityRemaining;
  }

  /**
   * Retrieves analytics for inventory within a specified date range.
   * @param {IfilterProps} filter - The filter criteria for the analytics period
   * @returns {Promise<IiventoryAnalytics>} The inventory analytics data
   * @throws {Error} If there's an error fetching the analytics
   */
  static getInventoryAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IiventoryAnalytics>(
        `${ItemInventory.baseUrl}/analytics`,
        filter
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves all inventory records with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: ItemInventory[] }>}
   * An object containing the total count and an array of inventory records
   * @throws {Error} If there's an error fetching the records
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TitemInventoryProps>>(`${ItemInventory.baseUrl}?${query}`);

    const itemInvs = await lastValueFrom(observer$);

    return {
      count: itemInvs.count,
      dataArr: itemInvs.data.map(val => new ItemInventory(val))
    };
  }

  /**
   * Filters inventory records based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: ItemInventory[] }>}
   * An object containing the total count and an array of filtered inventory records
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TitemInventoryProps>>(
        `${ItemInventory.baseUrl}/filter`,
        filter
      );

    const itemInvs = await lastValueFrom(observer$);

    return {
      count: itemInvs.count,
      dataArr: itemInvs.data.map(val => new ItemInventory(val))
    };
  }

  /**
   * Retrieves a single inventory record by its ID.
   * @param {string} resourceId - The unique identifier of the inventory record
   * @returns {Promise<ItemInventory>} The requested inventory record
   * @throws {Error} If the record is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TitemInventoryProps>(`${ItemInventory.baseUrl}/${resourceId}`);
    const itemInv = await lastValueFrom(observer$);

    return new ItemInventory(itemInv);
  }

  /**
   * Creates a new inventory record.
   * @param {TitemInventoryRequest<true>} payload - The inventory data to create
   * @returns {Promise<ItemInventory>} The newly created inventory record
   * @throws {Error} If the creation fails due to invalid data or server error
   */
  static async add(payload: TitemInventoryRequest<true>): Promise<ItemInventory> {
    const observer$ = CommerceClient.ehttp
      .makePost<TitemInventoryProps>(
        `${ItemInventory.baseUrl}`,
        payload
      );

    return new ItemInventory(await lastValueFrom(observer$));
  }
}
